import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, UsersRound, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { PlatformLogoBadge } from '@/client/pages/Admin/Media/components/PlatformLogo';
import { MEDIUM_LABEL, PLATFORM_LABEL } from '@/client/types/media';
import type { SocialPlatform, TraditionalMedium } from '@/client/types/media';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  MarketingFilterPanel,
  MarketingFilterSearch,
  MarketingFilterSelect,
  marketingFilterSelectClass,
  marketingMatchSummary,
} from './components/MarketingListFilters';
import { ActionIconButton } from './components/ActionIconButton';
import { StatusBadge, DeleteConfirmDialog, EmptyState } from './components/marketing-ui';
import {
  FieldHint,
  RequiredMark,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import {
  useCollaboratorsQuery,
  useCreateCollaboratorMutation,
  useDeleteCollaboratorMutation,
  useLinkCollaboratorSourceMutation,
  useUnlinkCollaboratorSourceMutation,
  useUpdateCollaboratorMutation,
} from '@/client/api/marketing/crew';
import { useDestinationsQuery } from '@/client/api/marketing/sources';
import type { ApiCollaborator, ApiCollaboratorSourceLink } from '@/client/types/marketing/api';
import {
  refFromApi,
  sameSource,
  sourceKey,
  type SourceRef,
} from '@/client/types/marketing/sources';
import { getApiErrorMessage } from '@/client/utils/errors';
import { COLLABORATOR_ROLE_LABEL, type CollaboratorRole } from '@/client/types/marketing';

/**
 * Crew → Contacts, on real data.
 *
 * This is the page where the id boundary actually bites, and where the shared
 * `SourceRef` earns its keep. The mock embedded `sourceLinks` on the contact and
 * keyed them by an opaque `acc_*` string; the API keeps them in their own table
 * addressed by an explicit (kind, id) pair. Carrying the pair means no parsing,
 * and a malformed value fails loudly instead of quietly attaching a press
 * contact to the wrong outlet.
 *
 * Links are saved separately from the contact, and deliberately so: linking is
 * idempotent on the pair server-side, so re-saving a contact whose links did not
 * change is a no-op rather than a churn of delete-and-recreate rows.
 */

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const ROLE_FILTERS: (CollaboratorRole | 'all')[] = [
  'all',
  'author',
  'editor',
  'reviewer',
  'approver',
  'designer',
  'social_manager',
];

/** Deterministic avatar tint. The API stores one, but new rows may not have it. */
const AVATAR_TONES = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-teal-500',
];
const avatarTone = (c: ApiCollaborator) =>
  c.avatar_color || AVATAR_TONES[c.mcid % AVATAR_TONES.length];

const initials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

/** A source the contact may be linked to, plus how to render it. */
interface SourceOption extends SourceRef {
  key: string;
  label: string;
  platform?: SocialPlatform;
  medium?: TraditionalMedium;
}

/** A pending link in the dialog, before it is saved. */
interface DraftLink extends SourceRef {
  isAccountHolder: boolean;
  /** Set when the link already exists server-side — needed to unlink it. */
  mcsid?: number;
}

type FormState = {
  name: string;
  email: string;
  phone: string;
  role: CollaboratorRole;
  active: boolean;
  notes: string;
  links: DraftLink[];
};

type FormErrors = Partial<Record<'name' | 'email', string>>;

const emptyForm: FormState = {
  name: '',
  email: '',
  phone: '',
  role: 'author',
  active: true,
  notes: '',
  links: [],
};

const linksOf = (c: ApiCollaborator): ApiCollaboratorSourceLink[] => c.source_links ?? [];

const toDraftLinks = (c: ApiCollaborator): DraftLink[] =>
  linksOf(c)
    .map((l): DraftLink | null => {
      const ref = refFromApi(l);
      // A link with neither said nor moid points at nothing. Dropping it is
      // right: it cannot be rendered, unlinked, or matched against an option.
      return ref ? { ...ref, isAccountHolder: !!l.is_account_holder, mcsid: l.mcsid } : null;
    })
    .filter((l): l is DraftLink => l !== null);

const CollaboratorsPage = () => {
  const { data, isLoading, isError, error } = useCollaboratorsQuery({
    limit: 200,
    with_sources: 1,
  });
  const destinations = useDestinationsQuery();
  const createContact = useCreateCollaboratorMutation();
  const updateContact = useUpdateCollaboratorMutation();
  const removeContact = useDeleteCollaboratorMutation();
  const linkSource = useLinkCollaboratorSourceMutation();
  const unlinkSource = useUnlinkCollaboratorSourceMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ApiCollaborator | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<ApiCollaborator | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<CollaboratorRole | 'all'>('all');
  const [holderFilter, setHolderFilter] = useState<'all' | 'holder' | 'contact_only'>('all');

  const collaborators: ApiCollaborator[] = data?.items ?? [];

  /**
   * Linkable sources.
   *
   * Reuses /sources/destinations rather than loading both registries: it already
   * returns the active accounts and outlets in one call, which is exactly the
   * set a contact can meaningfully speak for.
   */
  const sourceOptions: SourceOption[] = useMemo(() => {
    const accounts = (destinations.data?.accounts ?? []).map((a) => ({
      sourceKind: 'account' as const,
      sourceId: a.said,
      key: `account:${a.said}`,
      label: `${a.display_name} (${a.handle})`,
      platform: a.platform as SocialPlatform,
    }));
    const outlets = (destinations.data?.outlets ?? []).map((o) => ({
      sourceKind: 'outlet' as const,
      sourceId: o.moid,
      key: `outlet:${o.moid}`,
      label: `${o.name} · ${MEDIUM_LABEL[o.medium as TraditionalMedium] ?? o.medium}`,
      medium: o.medium as TraditionalMedium,
    }));
    return [...accounts, ...outlets].sort((a, b) => a.label.localeCompare(b.label));
  }, [destinations.data]);

  /** The server denormalises the source name onto the link, so no lookup needed. */
  const linkLabel = (l: ApiCollaboratorSourceLink) => {
    if (l.source_name && l.source_handle) {
      const platform = PLATFORM_LABEL[l.source_platform as SocialPlatform] ?? l.source_platform;
      return `${platform ?? ''} ${l.source_handle}`.trim();
    }
    if (l.source_name) return l.source_name;
    // The source row was removed but the link outlived it. Say so rather than
    // rendering a blank chip that looks like a rendering bug.
    return l.source_kind === 'account' ? 'Account removed' : 'Outlet removed';
  };

  useEffect(() => {
    if (!dialogOpen) return;
    setForm(
      editing
        ? {
            name: editing.name,
            email: editing.email,
            phone: editing.phone ?? '',
            role: editing.role as CollaboratorRole,
            active: !!editing.active,
            notes: editing.notes ?? '',
            links: toDraftLinks(editing),
          }
        : emptyForm,
    );
    setFormErrors({});
  }, [dialogOpen, editing]);

  const patchForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'name' || key === 'email') {
      setFormErrors((e) => clearFieldError(e, key));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return collaborators.filter((c) => {
      const links = linksOf(c);
      if (roleFilter !== 'all' && c.role !== roleFilter) return false;
      if (holderFilter === 'holder' && !links.some((l) => l.is_account_holder)) return false;
      if (
        holderFilter === 'contact_only' &&
        (links.length === 0 || links.every((l) => l.is_account_holder))
      ) {
        return false;
      }
      if (!q) return true;
      return [c.name, c.email, c.phone ?? '', c.notes ?? '', ...links.map(linkLabel)]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [collaborators, roleFilter, holderFilter, search]);

  const roleFilterOptions = useMemo(
    () =>
      ROLE_FILTERS.map((r) => ({
        value: r,
        label: r === 'all' ? 'All roles' : COLLABORATOR_ROLE_LABEL[r],
      })),
    [],
  );

  const holderFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All contacts' },
      { value: 'holder', label: 'Account holders' },
      { value: 'contact_only', label: 'Contact only (not holders)' },
    ],
    [],
  );

  const filtersActive =
    roleFilter !== 'all' || holderFilter !== 'all' || search.trim().length > 0;

  const clearFilters = () => {
    setRoleFilter('all');
    setHolderFilter('all');
    setSearch('');
  };

  const stats = useMemo(() => {
    const n = (value: number) => (isLoading ? 0 : value);
    return [
      { key: 'total', label: 'Contacts', value: n(collaborators.length) },
      { key: 'active', label: 'Active', value: n(collaborators.filter((c) => c.active).length) },
      {
        key: 'linked',
        label: 'Linked to sources',
        value: n(collaborators.filter((c) => linksOf(c).length > 0).length),
      },
      {
        key: 'holders',
        label: 'Account holders',
        value: n(
          collaborators.filter((c) => linksOf(c).some((l) => l.is_account_holder)).length,
        ),
      },
    ];
  }, [collaborators, isLoading]);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (c: ApiCollaborator) => {
    setEditing(c);
    setDialogOpen(true);
  };

  const toggleSource = (opt: SourceOption) => {
    setForm((f) => {
      const existing = f.links.find((l) => sameSource(l, opt));
      if (existing) return { ...f, links: f.links.filter((l) => !sameSource(l, opt)) };
      return {
        ...f,
        links: [
          ...f.links,
          { sourceKind: opt.sourceKind, sourceId: opt.sourceId, isAccountHolder: false },
        ],
      };
    });
  };

  const setHolder = (opt: SourceOption, isAccountHolder: boolean) => {
    setForm((f) => ({
      ...f,
      links: f.links.map((l) => (sameSource(l, opt) ? { ...l, isAccountHolder } : l)),
    }));
  };

  /**
   * Reconcile the dialog's links against what the server holds.
   *
   * Only the difference is written. Linking is idempotent on the pair, so a
   * holder flag that changed is one POST rather than an unlink and a relink —
   * which would briefly leave the contact detached from a source they never
   * stopped covering.
   */
  const saveLinks = async (mcid: number, before: DraftLink[], after: DraftLink[]) => {
    const removed = before.filter((b) => !after.some((a) => sameSource(a, b)));
    const changed = after.filter((a) => {
      const prior = before.find((b) => sameSource(b, a));
      return !prior || prior.isAccountHolder !== a.isAccountHolder;
    });

    for (const link of removed) {
      if (link.mcsid == null) continue;
      await unlinkSource.mutateAsync({
        mcid,
        mcsid: link.mcsid,
        source_kind: link.sourceKind,
        source_id: link.sourceId,
      });
    }
    for (const link of changed) {
      await linkSource.mutateAsync({
        mcid,
        source_kind: link.sourceKind,
        source_id: link.sourceId,
        is_account_holder: link.isAccountHolder,
      });
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FormErrors = {};
    if (!form.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!form.email.trim()) {
      errors.email = 'Email is required';
    } else if (!form.email.includes('@')) {
      errors.email = 'Enter a valid email address';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Fix the highlighted fields before saving');
      return;
    }

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      role: form.role,
      active: form.active ? 1 : 0,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      if (editing) {
        await updateContact.mutateAsync({ mcid: editing.mcid, ...payload });
        await saveLinks(editing.mcid, toDraftLinks(editing), form.links);
        toast.success('Contact updated');
      } else {
        const created = await createContact.mutateAsync(payload);
        const mcid = created?.data?.mcid;
        if (mcid) {
          await saveLinks(mcid, [], form.links);
        } else if (form.links.length) {
          // The contact exists but we cannot attach its sources without an id.
          // Saying so beats a success toast that hides half the work.
          toast.warning('Contact created, but its source links could not be attached');
        }
        toast.success('Contact added');
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save the contact'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const name = deleting.name;
    removeContact.mutate(
      { mcid: deleting.mcid },
      {
        onSuccess: () => toast.success(`Removed ${name}`),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not remove the contact')),
      },
    );
    setDeleting(null);
  };

  return (
    <MarketingPageShell
      title="Contacts"
      description="Contact people for specific accounts and outlets — press desks, creators, brand managers. They may or may not hold the platform account."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Crew' },
        { label: 'Contacts' },
      ]}
      icon={UsersRound}
      actions={
        <Button type="button" className="gap-2" onClick={openAdd}>
          <Plus className="h-4 w-4" /> Add contact
        </Button>
      }
      stats={stats}
    >
      <p className="mb-4 text-sm text-gray-600">
        Contacts appear on the sources they cover under{' '}
        <Link
          to="/admin/marketing/sources/accounts"
          className="font-medium text-blue-700 hover:underline"
        >
          Accounts
        </Link>{' '}
        and{' '}
        <Link
          to="/admin/marketing/sources/outlets"
          className="font-medium text-blue-700 hover:underline"
        >
          Outlets
        </Link>
        . Workflow roles (author, approver, …) still apply for content and Approvals.
      </p>

      <MarketingFilterPanel
        summary={marketingMatchSummary(filtered.length, collaborators.length, 'contact', isLoading)}
        showClear={filtersActive}
        onClear={clearFilters}
      >
        <MarketingFilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search name, email, source…"
        />
        <MarketingFilterSelect
          options={roleFilterOptions}
          value={roleFilter}
          onChange={(value) => setRoleFilter(value as CollaboratorRole | 'all')}
          placeholder="All roles"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <MarketingFilterSelect
          options={holderFilterOptions}
          value={holderFilter}
          onChange={(value) => setHolderFilter(value as typeof holderFilter)}
          placeholder="All contacts"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
      </MarketingFilterPanel>

      {isLoading ? (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500 shadow-sm">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading contacts…
        </div>
      ) : isError ? (
        /* An unreachable API and an empty directory look identical if you only
           render "no contacts" — say which it is. */
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-16 text-center text-sm shadow-sm">
          <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
          <div className="font-medium text-amber-800">Could not load contacts</div>
          <div className="mt-1 text-xs text-gray-500">
            {getApiErrorMessage(error, 'The marketing API did not respond')}
          </div>
        </div>
      ) : collaborators.length === 0 ? (
        <EmptyState label="No contacts yet — add the people you actually email about each account and outlet." />
      ) : filtered.length === 0 ? (
        <EmptyState label="No contacts match these filters." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <TooltipProvider delayDuration={200}>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Sources</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((c) => {
                const links = linksOf(c);
                return (
                  <tr
                    key={c.mcid}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => openEdit(c)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold text-white ${avatarTone(c)}`}
                        >
                          {initials(c.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900">{c.name}</p>
                          <p className="truncate text-xs text-gray-500">{c.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {links.length === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {links.map((l) => (
                            <span
                              key={l.mcsid}
                              className={cn(
                                'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px]',
                                l.is_account_holder
                                  ? 'bg-sky-50 text-sky-800'
                                  : 'bg-gray-100 text-gray-700',
                              )}
                              title={l.is_account_holder ? 'Account holder' : 'Contact only'}
                            >
                              {linkLabel(l)}
                              {l.is_account_holder ? ' · holder' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {COLLABORATOR_ROLE_LABEL[c.role as CollaboratorRole] ?? c.role}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        label={c.active ? 'Active' : 'Inactive'}
                        className={
                          c.active
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-gray-100 text-gray-500'
                        }
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ActionIconButton
                        label="Remove"
                        stopPropagation
                        onClick={() => setDeleting(c)}
                        className="text-gray-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </ActionIconButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TooltipProvider>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit contact' : 'Add contact'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} noValidate className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={<><span>Name</span> <RequiredMark /></>}>
                <input
                  value={form.name}
                  onChange={(e) => patchForm('name', e.target.value)}
                  className={fieldClass(inputClass, formErrors.name)}
                  aria-invalid={!!formErrors.name}
                />
                <FieldHint error={formErrors.name} />
              </Field>
              <Field label={<><span>Email</span> <RequiredMark /></>}>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => patchForm('email', e.target.value)}
                  className={fieldClass(inputClass, formErrors.email)}
                  aria-invalid={!!formErrors.email}
                />
                <FieldHint error={formErrors.email} />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Phone (optional)">
                <input
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className={inputClass}
                  placeholder="+233 …"
                />
              </Field>
              <Field label="Workflow role">
                <select
                  value={form.role}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, role: e.target.value as CollaboratorRole }))
                  }
                  className={inputClass}
                >
                  {Object.entries(COLLABORATOR_ROLE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-600">Sources (accounts &amp; outlets)</p>
              <p className="text-xs text-gray-500">
                Mark “Account holder” when this person controls the login or primary relationship —
                leave unchecked for press desks, managers, and other contacts.
              </p>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-gray-100 p-2">
                {destinations.isLoading && (
                  <p className="px-2 py-3 text-xs text-gray-400">Loading sources…</p>
                )}
                {!destinations.isLoading && destinations.isError && (
                  <p className="px-2 py-3 text-xs text-amber-700">
                    Could not load sources — the contact can still be saved, and links added later.
                  </p>
                )}
                {!destinations.isLoading && !destinations.isError && sourceOptions.length === 0 && (
                  <p className="px-2 py-3 text-xs text-gray-400">
                    No active sources yet. Add accounts or outlets first.
                  </p>
                )}
                {sourceOptions.map((opt) => {
                  const link = form.links.find((l) => sameSource(l, opt));
                  const checked = !!link;
                  return (
                    <div
                      key={sourceKey(opt)}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                        checked ? 'bg-blue-50' : 'hover:bg-gray-50',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSource(opt)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      {opt.platform ? (
                        <PlatformLogoBadge platform={opt.platform} size="sm" />
                      ) : null}
                      <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                      {checked && (
                        <label className="flex shrink-0 items-center gap-1 text-[11px] text-gray-600">
                          <input
                            type="checkbox"
                            checked={link!.isAccountHolder}
                            onChange={(e) => setHolder(opt, e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-gray-300"
                          />
                          Holder
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="min-h-[64px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
                placeholder="Pitch window, preferred channel, manager vs creator…"
              />
            </Field>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300"
              />
              Active
            </label>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add contact'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Remove contact?"
        description={`"${deleting?.name}" will be removed and unlinked from every source they cover. This cannot be undone.`}
        onConfirm={confirmDelete}
      />
    </MarketingPageShell>
  );
};

export default CollaboratorsPage;
